export class CappedMap<K, V> {
  private readonly limit: number;
  private readonly map: Map<K, V>;
  private onEvict?: (key: K, value: V) => void;

  constructor(limit: number, entries?: readonly (readonly [K, V])[]) {
    if (!Number.isFinite(limit) || limit <= 0) {
      throw new Error('limit must be a positive finite number');
    }
    this.limit = limit;
    this.map = new Map(entries);

    // If initial entries exceed limit, evict oldest until within limit
    while (this.map.size > this.limit) {
      const oldestKey = this.map.keys().next().value as K;
      this.map.delete(oldestKey);
    }
  }

  /** Set a callback to be invoked when an entry is evicted */
  setOnEvict(callback: (key: K, value: V) => void): void {
    this.onEvict = callback;
  }

  set(key: K, value: V): this {
    // If key exists, delete first so insertion order updates
    if (this.map.has(key)) this.map.delete(key);
    this.map.set(key, value);

    // Evict oldest (FIFO)
    if (this.map.size > this.limit) {
      const oldestKey = this.map.keys().next().value as K;
      const oldestValue = this.map.get(oldestKey);
      this.onEvict?.(oldestKey, oldestValue);
      this.map.delete(oldestKey);
    }
    return this;
  }

  get(key: K): V | undefined {
    return this.map.get(key);
  }

  has(key: K): boolean {
    return this.map.has(key);
  }

  delete(key: K): boolean {
    return this.map.delete(key);
  }

  clear(): void {
    this.map.clear();
  }

  get size(): number {
    return this.map.size;
  }

  keys(): IterableIterator<K> {
    return this.map.keys();
  }

  values(): IterableIterator<V> {
    return this.map.values();
  }

  entries(): IterableIterator<[K, V]> {
    return this.map.entries();
  }

  [Symbol.iterator](): IterableIterator<[K, V]> {
    return this.map[Symbol.iterator]();
  }

  /** Oldest entry (FIFO), if present */
  oldest(): [K, V] | undefined {
    const first = this.map.entries().next();
    return first.done ? undefined : first.value;
  }

  /** Newest entry, if present */
  newest(): [K, V] | undefined {
    // Map doesn't give "last" directly; iterate once.
    let last: [K, V] | undefined;
    for (const e of this.map) last = e;
    return last;
  }

  /** Convert to a real Map copy (type-safe) */
  toMap(): Map<K, V> {
    return new Map(this.map);
  }
}
